import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

const SponsorDonationOverview = () => {
  const { user } = useContext(AuthContext);
  const { t } = useContext(LanguageContext);
  const [donations, setDonations] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/donations/by-sponsor`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = await res.json();
        if (res.ok) {
          setDonations(data.donations || []);
          setTotalAmount(Number(data.total_amount) || 0);
          setTotalTokens(Number(data.total_tokens) || 0);
        }
      } catch (err) {
        console.error("Error fetching donations:", err);
      }
    };

    fetchDonations();
  }, [user]);

  const columns = useMemo(
    () => [
      {
        header: t("date"),
        accessorKey: "date",
        cell: (info) => formatDate(info.getValue()),
      },
      {
        header: t("recipient"),
        accessorKey: "recipientName",
      },
      {
        header: t("tokens"),
        accessorKey: "tokens",
      },
      {
        header: t("amount"),
        accessorKey: "amount",
        cell: (info) => `${Number(info.getValue() || 0).toFixed(2)} €`,
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: donations,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 max-w-7xl mx-auto">
      <div className="bg-white shadow-md rounded-xl p-8 animate-fadeIn">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t("myDonations")}
        </h1>
        <p className="text-gray-500 mb-6">
          {t("totalAmount")}:{" "}
          <span className="text-green-600 font-semibold">
            {Number(totalAmount).toFixed(2)} €
          </span>{" "}
          | {t("totalTokens")}:{" "}
          <span className="text-blue-600 font-semibold">{totalTokens}</span>
        </p>

        {/* 🔍 Global Search */}
        <input
          type="text"
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t("search") || "Search donations..."}
          className="mb-4 px-4 py-2 border rounded w-full shadow-sm"
        />

        {/* 🧾 Table */}
        <table className="w-full table-auto bg-white border">
          <thead className="bg-gray-100 text-gray-700 text-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-left cursor-pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted()
                      ? header.column.getIsSorted() === "asc"
                        ? " 🔼"
                        : " 🔽"
                      : ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="text-gray-700 text-sm">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* ⏮️ Pagination */}
        <div className="flex justify-between items-center mt-4 text-sm">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-4 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            {t("Previous") || "Previous"}
          </button>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-4 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            {t("Next") || "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorDonationOverview;
